import React, { type CSSProperties, type HTMLAttributes, type ReactNode } from "react";
import { cx } from "./fieldContext";
const BREAKPOINT_SM_MIN = 576;

/*
 * Raster des Kits (Plan 2026-10-13-antd-ausbau, Stufe 4): `Row`, `Col` (24 Spalten) und `Grid.useBreakpoint`
 * mit denselben Breakpoints wie Antd (xs < 576, sm ≥ 576, md ≥ 768, lg ≥ 992, xl ≥ 1200, xxl ≥ 1600).
 * Spaltenbreiten über Klassen in `ui/grid.css`, Abstände über CSS-Variablen.
 */

export type Breakpoint = "xs" | "sm" | "md" | "lg" | "xl" | "xxl";
const BREAKPOINT_MIN: Record<Breakpoint, number> = { xs: 0, sm: 576, md: 768, lg: 992, xl: 1200, xxl: 1600 };
const BREAKPOINTS = Object.keys(BREAKPOINT_MIN) as Breakpoint[];

export type ScreenMap = Partial<Record<Breakpoint, boolean>>;

function readScreens(): ScreenMap {
  if (typeof window === "undefined") return {};
  const width = window.innerWidth;
  const viaMedia = typeof window.matchMedia === "function";
  const screens: ScreenMap = {};
  for (const bp of BREAKPOINTS) {
    if (bp === "xs") screens.xs = viaMedia ? window.matchMedia("(max-width: 575.98px)").matches : width < BREAKPOINT_SM_MIN;
    else screens[bp] = viaMedia ? window.matchMedia(`(min-width: ${BREAKPOINT_MIN[bp]}px)`).matches : width >= BREAKPOINT_MIN[bp];
  }
  return screens;
}

let snapshot: ScreenMap = readScreens();
const listeners = new Set<() => void>();
function refresh() {
  const next = readScreens();
  if (BREAKPOINTS.some((bp) => next[bp] !== snapshot[bp])) {
    snapshot = next;
    listeners.forEach((listener) => listener());
  }
}
function subscribe(listener: () => void) {
  listeners.add(listener);
  if (listeners.size === 1 && typeof window !== "undefined") window.addEventListener("resize", refresh);
  refresh();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && typeof window !== "undefined") window.removeEventListener("resize", refresh);
  };
}

/** Aktive Breakpoints wie Antd `Grid.useBreakpoint()`; aktualisiert sich bei Größenänderung. */
export function useBreakpoint(): ScreenMap {
  return React.useSyncExternalStore(subscribe, () => snapshot, () => ({}));
}

// ---------------------------------------------------------------- Row

type Gap = number | string | undefined;
export interface RowProps extends HTMLAttributes<HTMLDivElement> {
  gutter?: Gap | [Gap, Gap] | Partial<Record<Breakpoint, number>>;
  align?: "top" | "middle" | "bottom" | "stretch" | "start" | "end" | "center" | "baseline";
  justify?: "start" | "end" | "center" | "space-around" | "space-between" | "space-evenly";
  wrap?: boolean;
  children?: ReactNode;
}

function gapValue(value: Gap): string {
  if (value == null) return "0px";
  return typeof value === "number" ? `${value}px` : value;
}

function resolveGutter(gutter: RowProps["gutter"], screens: ScreenMap): [string, string] {
  if (gutter == null) return ["0px", "0px"];
  if (Array.isArray(gutter)) return [gapValue(gutter[0]), gapValue(gutter[1])];
  if (typeof gutter === "object") {
    const active = [...BREAKPOINTS].reverse().find((bp) => screens[bp] && (gutter as Record<string, number>)[bp] != null);
    return [gapValue(active ? (gutter as Record<string, number>)[active] : 0), "0px"];
  }
  return [gapValue(gutter), "0px"];
}

const ALIGN: Record<NonNullable<RowProps["align"]>, CSSProperties["alignItems"]> = {
  top: "flex-start", start: "flex-start", middle: "center", center: "center", bottom: "flex-end", end: "flex-end", stretch: "stretch", baseline: "baseline",
};

export const Row = React.forwardRef<HTMLDivElement, RowProps>(function Row({ gutter, align, justify, wrap = true, className, style, children, ...rest }, ref) {
  const screens = useBreakpoint();
  const [gx, gy] = resolveGutter(gutter, typeof gutter === "object" && !Array.isArray(gutter) ? screens : {});
  const vars = { "--ck-row-gx": gx, "--ck-row-gy": gy } as CSSProperties;
  return (
    <div
      ref={ref}
      className={cx("ck-row", !wrap && "ck-row--nowrap", className)}
      style={{ ...vars, alignItems: align ? ALIGN[align] : undefined, justifyContent: justify === "start" ? "flex-start" : justify === "end" ? "flex-end" : justify, ...style }}
      {...rest}
    >
      {children}
    </div>
  );
});

// ---------------------------------------------------------------- Col

type ColSpan = number | string;
export interface ColProps extends HTMLAttributes<HTMLDivElement> {
  span?: ColSpan;
  offset?: number;
  order?: number;
  flex?: number | string;
  xs?: ColSpan | { span?: ColSpan; offset?: number; order?: number };
  sm?: ColProps["xs"];
  md?: ColProps["xs"];
  lg?: ColProps["xs"];
  xl?: ColProps["xs"];
  xxl?: ColProps["xs"];
  children?: ReactNode;
}

function flexValue(flex: number | string): string {
  if (typeof flex === "number") return `${flex} ${flex} auto`;
  if (/^\d+(\.\d+)?(px|em|rem|%)$/.test(flex)) return `0 0 ${flex}`;
  return flex;
}

export const Col = React.forwardRef<HTMLDivElement, ColProps>(function Col({ span, offset, order, flex, xs, sm, md, lg, xl, xxl, className, style, children, ...rest }, ref) {
  const classes: string[] = ["ck-col"];
  if (span != null) classes.push(`ck-col-${span}`);
  if (offset) classes.push(`ck-col-offset-${offset}`);
  const responsive: Record<Breakpoint, ColProps["xs"]> = { xs, sm, md, lg, xl, xxl };
  for (const bp of BREAKPOINTS) {
    const value = responsive[bp];
    if (value == null) continue;
    if (typeof value === "object") {
      if (value.span != null) classes.push(`ck-col-${bp}-${value.span}`);
      if (value.offset) classes.push(`ck-col-${bp}-offset-${value.offset}`);
      if (value.order != null) classes.push(`ck-col-${bp}-order-${value.order}`);
    } else classes.push(`ck-col-${bp}-${value}`);
  }
  return (
    <div ref={ref} className={cx(...classes, className)} style={{ ...(flex != null ? { flex: flexValue(flex), maxWidth: "100%" } : {}), ...(order != null ? { order } : {}), ...style }} {...rest}>
      {children}
    </div>
  );
});

/** Ersatz für Antd `Grid` (nur `useBreakpoint`). */
export const Grid = { useBreakpoint };
